import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { useCreateDealer, useDealerDashboardSummary, useDealers, useDeleteDealer } from '../api/hooks';
import { MAX_VIDEO_SECONDS } from '../components/investor/documentRules';
import PendingDocumentPicker from '../components/investor/PendingDocumentPicker';
import type { DealerListItem, DocumentType, RecentTransaction } from '../types';
import { apiDate, date, money } from '../utils/format';
import { paymentMethodOptions } from '../utils/paymentMethods';

const MOBILE_PATTERN = /^03\d{2}-\d{7}$/;
const CNIC_PATTERN = /^\d{5}-\d{7}-\d$/;

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  'Profit Payment': 'green',
};

/**
 * The admin's own placements with dealers — laid out exactly like the main Dashboard + Investors
 * pages combined: KPI cards, two charts, a recent-transactions feed, then the searchable list.
 * Same shapes, same words, so nothing here needs a separate mental model to read.
 */
export default function DealersPage() {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: summary, isLoading: loadingSummary, error: summaryError } = useDealerDashboardSummary();
  const { data, isLoading } = useDealers(search || undefined);
  const deleteDealer = useDeleteDealer();

  const onDelete = (row: DealerListItem) =>
    Modal.confirm({
      title: `Delete ${row.dealerCode} — ${row.fullName}?`,
      content:
        'This permanently removes the dealer along with every investment, withdrawal and profit payment on record. This cannot be undone.',
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteDealer.mutateAsync(row.id);
          message.success('Dealer deleted.');
        } catch (err) {
          Modal.error({ title: 'Could not delete this dealer', content: errorMessage(err) });
        }
      },
    });

  const kpis = summary
    ? [
        { title: 'Total dealers', value: summary.totalDealers, suffix: `/ ${summary.activeDealers} active` },
        { title: 'Total capital', value: money(summary.totalCapital), prefix: 'PKR' },
        { title: "Today's due profit", value: money(summary.todayDueProfit), prefix: 'PKR' },
        { title: 'Received this month', value: money(summary.thisMonthReceivedProfit), prefix: 'PKR' },
        { title: 'Outstanding profit', value: money(summary.outstandingProfit), prefix: 'PKR' },
        { title: 'Capital + liabilities', value: money(summary.totalCapitalPlusLiabilities), prefix: 'PKR' },
      ]
    : [];

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          My Investment Dashboard
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          Add dealer
        </Button>
      </Space>

      {summaryError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not load the dashboard summary."
          description={errorMessage(summaryError)}
        />
      ) : loadingSummary || !summary ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {kpis.map((kpi) => (
              <Col key={kpi.title} xs={24} sm={12} lg={8} xl={4}>
                <Card>
                  <Statistic title={kpi.title} value={kpi.value} prefix={kpi.prefix} suffix={kpi.suffix} valueStyle={{ fontSize: 20 }} />
                </Card>
              </Col>
            ))}
          </Row>

          <Card title="Profit received — last 12 months" style={{ marginTop: 16 }}>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={summary.monthlyReceivedProfit} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} />
                  <YAxis tickFormatter={(v: number) => money(v)} width={90} tickLine={false} />
                  <ChartTooltip formatter={(v: number) => `PKR ${money(v)}`} />
                  <Bar dataKey="amount" fill="#1677ff" radius={[4, 4, 0, 0]} name="Profit received" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Capital given vs received — last 12 months" style={{ marginTop: 16 }}>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={summary.monthlyCapitalFlow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} />
                  <YAxis tickFormatter={(v: number) => money(v)} width={90} tickLine={false} />
                  <ChartTooltip formatter={(v: number) => `PKR ${money(v)}`} />
                  <Legend />
                  <Bar dataKey="given" fill="#cf1322" radius={[4, 4, 0, 0]} name="Capital given" />
                  <Bar dataKey="received" fill="#3f8600" radius={[4, 4, 0, 0]} name="Capital received" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Recent transactions" style={{ marginTop: 16 }}>
            <Table<RecentTransaction>
              dataSource={summary.recentTransactions}
              rowKey={(row, index) => `${row.investorCode}-${row.date}-${index}`}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Date', dataIndex: 'date', render: date },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{value}</Tag>,
                },
                { title: 'Code', dataIndex: 'investorCode' },
                { title: 'Dealer', dataIndex: 'investorName' },
                { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
              ]}
            />
          </Card>
        </>
      )}

      <Card style={{ marginTop: 16 }}>
        <Input.Search
          allowClear
          placeholder="Search by name, mobile, CNIC or code"
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={setSearch}
        />

        <Table<DealerListItem>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          scroll={{ x: 'max-content' }}
          onRow={(row) => ({ onClick: () => navigate(`/my-investments/${row.id}`), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'Code', dataIndex: 'dealerCode' },
            { title: 'Name', dataIndex: 'fullName' },
            { title: 'Mobile', dataIndex: 'mobile', render: (v: string | null) => v ?? '—' },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (v: string) => <Tag color={v === 'Active' ? 'green' : 'default'}>{v}</Tag>,
            },
            { title: 'Total investment', dataIndex: 'currentPrincipal', align: 'right', render: money },
            {
              title: 'Outstanding profit',
              dataIndex: 'outstandingProfit',
              align: 'right',
              render: (v: number) => <span style={{ color: v > 0 ? '#cf1322' : undefined }}>{money(v)}</span>,
            },
            {
              title: 'Next due date',
              render: (_, row) =>
                !row.nextExpectedReturn ? (
                  '—'
                ) : (
                  <Space direction="vertical" size={0}>
                    <span>{date(row.nextExpectedReturn)}</span>
                    <Tag color={row.hasOverdue ? 'red' : row.daysToNextReturn <= 7 ? 'orange' : 'blue'}>
                      {row.daysToNextReturn < 0
                        ? `${Math.abs(row.daysToNextReturn)} days overdue`
                        : `${row.daysToNextReturn} days left`}
                    </Tag>
                  </Space>
                ),
            },
            {
              title: 'Actions',
              render: (_, row) => (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button type="link" size="small" onClick={() => navigate(`/my-investments/${row.id}`)}>
                    View
                  </Button>
                  <Button danger type="link" size="small" onClick={() => onDelete(row)}>
                    Delete
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <CreateDealerModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * Mirrors CreateInvestorModal field for field: identity, payment details, then documents, then an
 * optional initial investment. Documents can only be attached once the dealer row exists, so — same
 * as investors — they are uploaded right after creation, and a failed upload does not undo it.
 */
function CreateDealerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const createDealer = useCreateDealer();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState<File | null>(null);
  const [cnicImage, setCnicImage] = useState<File | null>(null);
  const [agreementVideo, setAgreementVideo] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const resetAll = () => {
    form.resetFields();
    setPhoto(null);
    setCnicImage(null);
    setAgreementVideo(null);
  };

  const uploadPending = async (dealerId: number): Promise<string[]> => {
    const pending: Array<[DocumentType, File | null]> = [
      ['photo', photo],
      ['cnic-image', cnicImage],
      ['agreement-video', agreementVideo],
    ];

    const failed: string[] = [];
    for (const [type, file] of pending) {
      if (!file) continue;
      try {
        const body = new FormData();
        body.append('file', file);
        await api.post(`/dealers/${dealerId}/documents/${type}`, body, {
          headers: { 'Content-Type': undefined },
        });
      } catch {
        failed.push(type);
      }
    }
    return failed;
  };

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      const result = await createDealer.mutateAsync({
        ...values,
        initialPlacementDate: apiDate(values.initialPlacementDate as dayjs.Dayjs | undefined),
        initialExpectedReturnDate: apiDate(values.initialExpectedReturnDate as dayjs.Dayjs | undefined),
      });

      setUploading(true);
      const failedUploads = await uploadPending(result.dealer.id);
      setUploading(false);
      if (failedUploads.length > 0) {
        message.warning(
          `Dealer created, but these files did not upload: ${failedUploads.join(', ')}. Add them from the profile tab.`,
        );
      }

      // The generated password is shown once and never returned again — same rule as investors.
      Modal.success({
        title: 'Dealer created',
        width: 480,
        content: (
          <div>
            <p>
              Code / username: <strong>{result.username}</strong>
            </p>
            <p>
              Temporary password: <strong>{result.generatedPassword}</strong>
            </p>
            <p style={{ color: '#cf1322' }}>Save this password now — it will not be shown again.</p>
          </div>
        ),
      });
      resetAll();
      onClose();
      navigate(`/my-investments/${result.dealer.id}`);
    } catch (err) {
      setUploading(false);
      message.error(errorMessage(err));
    }
  };

  return (
    <Modal
      title="Add dealer"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={createDealer.isPending || uploading}
      okText="Create"
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
        <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="fatherName" label="Father's name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="mobile"
          label="Mobile"
          rules={[{ required: true }, { pattern: MOBILE_PATTERN, message: 'Format: 03XX-XXXXXXX' }]}
        >
          <Input placeholder="0300-1234567" />
        </Form.Item>
        <Form.Item
          name="cnic"
          label="CNIC"
          rules={[{ required: true }, { pattern: CNIC_PATTERN, message: 'Format: XXXXX-XXXXXXX-X' }]}
          help="Cannot be changed after the account is created."
        >
          <Input placeholder="35202-1234567-1" />
        </Form.Item>
        <Form.Item name="address" label="Address" rules={[{ required: true }]} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="paymentMethod" label="Payment method" rules={[{ required: true }]}>
          <Select
            placeholder="Select how money moves to and from this investor"
            options={paymentMethodOptions}
          />
        </Form.Item>
        <Form.Item name="accountNumber" label="Account / wallet number" rules={[{ required: true }]}>
          <Input placeholder="Account title, IBAN or wallet number" />
        </Form.Item>

        <Divider orientation="left" plain style={{ marginTop: 8 }}>
          Documents
        </Divider>
        <PendingDocumentPicker
          type="photo"
          label="Profile photo"
          hint="JPG, PNG or WebP · max 5 MB"
          file={photo}
          onChange={setPhoto}
        />
        <PendingDocumentPicker
          type="cnic-image"
          label="CNIC image"
          hint="JPG, PNG or WebP · max 5 MB"
          file={cnicImage}
          onChange={setCnicImage}
        />
        <PendingDocumentPicker
          type="agreement-video"
          label="Agreement video"
          hint={`MP4 or WebM · max ${MAX_VIDEO_SECONDS} seconds · 25 MB`}
          file={agreementVideo}
          onChange={setAgreementVideo}
        />

        <Divider orientation="left" plain>
          Initial investment
        </Divider>
        <Form.Item name="initialAmount" label="Amount" rules={[{ required: true }]} style={{ marginTop: 8 }}>
          <InputNumber min={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="initialProfitRate"
          label="Monthly profit rate (%)"
          help="Optional. Leave blank if the rate is not agreed yet — no profit accrues until it is set."
        >
          <InputNumber min={0} max={99.99} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="initialPlacementDate" label="Investment date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="initialExpectedReturnDate" label="Profit expected on" help="Used for the countdown on the dealer's page.">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
